import { useEffect, useState } from "react";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { AUTH_CONSTANTS } from "@/constants";
import { getActivePartnerName } from "@/components/partner/PartnerSwitcherMenuItem";

export interface ActivePartner {
  partnerName: string | null;
  // undefined until resolved — callers must suppress definitive UI while unknown.
  readOnly?: boolean;
  authMode?: string;
  isLoading: boolean;
}

interface AccessiblePartnerLite {
  partnerId: string;
  partnerName: string;
  readOnly?: boolean;
  authMode?: string;
  isHomePartner?: boolean;
}

export const useActivePartner = (enabled = true): ActivePartner => {
  const [state, setState] = useState<ActivePartner>({
    partnerName: getActivePartnerName(),
    readOnly: undefined,
    isLoading: enabled,
  });

  useEffect(() => {
    if (!enabled) {
      setState({ partnerName: getActivePartnerName(), readOnly: undefined, isLoading: false });
      return;
    }

    let cancelled = false;

    // Mark as loading whenever (re)enabled so consumers suppress output until
    // the read-only state is known — even across the enabled false→true flip.
    setState((s) => ({ ...s, readOnly: undefined, isLoading: true }));

    const resolve = async () => {
      try {
        const { json } = await httpClient(`${getPartnerAwareApiUrl()}/partners/accessible`);
        const partners: AccessiblePartnerLite[] = json?.partners ?? [];
        const activeId = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.ACTIVE_PARTNER_ID);
        const active =
          (activeId && partners.find((p) => p.partnerId === activeId)) || partners.find((p) => p.isHomePartner) || null;

        if (cancelled) return;

        if (active) {
          setState({
            partnerName: active.partnerName ?? getActivePartnerName(),
            readOnly: Boolean(active.readOnly),
            authMode: active.authMode,
            isLoading: false,
          });
        } else {
          setState({ partnerName: getActivePartnerName(), readOnly: false, isLoading: false });
        }
      } catch {
        if (cancelled) return;
        setState({ partnerName: getActivePartnerName(), readOnly: false, isLoading: false });
      }
    };

    void resolve();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return state;
};
